// 代理入力フォーム（DM 等で受領した住所を担当者が登録する）。貼り付け→郵便番号/住所/電話/氏名に自動分割（手直し可）。
import { useEffect, useState } from "react";
import type { GfCampaign, GfCreator, GfEntry, GfTabProps } from "../../../../types/gifting";
import { saveGfProxyAddress } from "../../../../api/giftingClient";
import { GF_PHASE, normalizePhone, normalizePostal, parsePastedAddress, shipErrorText } from "../../../../lib/giftingRules";
import { GfButton, GfField } from "../../ui/GfUi";
import { errMsg } from "../kit/helpers";

interface Errors {
  name?: string;
  postal?: string;
  address?: string;
  phone?: string;
  product?: string;
  email?: string;
}

export function ProxyAddressForm({
  entry,
  creator,
  campaign,
  refresh,
  toast,
  onSaved,
}: {
  entry: GfEntry;
  creator: GfCreator | null;
  /** choice 案件ならセットの選択欄を出す */
  campaign: GfCampaign | null;
  /** 登録後に呼ぶ（第2弾: 検証OKなら出荷指示を送れるようエントリー詳細を開く） */
  onSaved?: (validationOk: boolean) => void;
} & Pick<GfTabProps, "refresh" | "toast">) {
  const [received, setReceived] = useState("Instagram DM");
  const [raw, setRaw] = useState("");
  // 宛名はアカウント名で埋めない（表示名のまま倉庫へ出てしまうため。アカウント名は見出しに別表示・2026-11-05）
  const [name, setName] = useState("");
  const [postal, setPostal] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const choice = campaign?.product_mode === "choice" && !entry.ship_order_no;
  const [sku, setSku] = useState(entry.sku ?? "");

  // 対象エントリーが変わったら入力をリセット
  useEffect(() => {
    setReceived("Instagram DM");
    setRaw("");
    setName("");
    setPostal("");
    setAddress("");
    setPhone("");
    setEmail("");
    setSku(entry.sku ?? "");
    setErrors({});
    // creator は entry から決まる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id]);

  const onRaw = (v: string) => {
    setRaw(v);
    const p = parsePastedAddress(v);
    if (p.name) setName(p.name);
    if (p.postal) setPostal(`${p.postal.slice(0, 3)}-${p.postal.slice(3)}`);
    if (p.address) setAddress(p.address);
    if (p.phone) setPhone(p.phone);
  };

  const submit = async () => {
    const next: Errors = {};
    const pc = normalizePostal(postal);
    const ph = normalizePhone(phone);
    if (!name.trim()) next.name = "お名前を入力してください";
    if (!pc) next.postal = "郵便番号は7桁の数字で入力してください";
    if (!address.trim()) next.address = "住所を入力してください";
    if (!ph) next.phone = "電話番号は 0 から始まる10〜11桁で入力してください";
    if (choice && !sku) next.product = "お届けするセットを選んでください";
    const em = email.trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) next.email = "メールアドレスの形をご確認ください";
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      const res = await saveGfProxyAddress(entry.id, {
        ship_name: name.trim(),
        ship_postal: pc,
        ship_address: address.trim(),
        ship_phone: ph,
        received_via: received.trim() || undefined,
        ...(choice && sku ? { sku } : {}),
        ...(em ? { email: em } : {}),
      });
      const ok = !res.validation || res.validation.ok;
      if (!ok) toast(`住所を登録しましたが、出荷内容の確認が必要です: ${shipErrorText(res.validation?.errors ?? [])}`);
      else toast(GF_PHASE >= 2 ? "住所を登録しました。内容を確認して「出荷指示を送る」で倉庫へ送れます" : "住所を登録しました");
      setRaw("");
      await refresh();
      onSaved?.(ok);
    } catch (e) {
      toast(`登録できませんでした: ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const product = `${entry.product_name}${entry.quantity > 1 ? ` × ${entry.quantity}` : ""}${entry.sku ? `（SKU ${entry.sku}）` : ""}`;

  return (
    <>
      <div className="gf-form-head">
        <span className="gf-form-title">代理入力：{creator?.name ?? "—"}</span>
        <span className="gf-note">DMで受領した住所を担当者が登録します</span>
      </div>
      <GfField label="受領元" hint="例）Instagram DM・10/03">
        <input className="gf-input" value={received} onChange={(e) => setReceived(e.target.value)} maxLength={100} />
      </GfField>
      <GfField label="お届け先住所（貼り付けで自動分割）">
        <textarea
          className="gf-textarea"
          value={raw}
          onChange={(e) => onRaw(e.target.value)}
          placeholder={"DMの文面をそのまま貼り付けると、郵便番号・住所・電話番号に分割されます"}
        />
      </GfField>
      <GfField label="お届け先のお名前（宅配便の宛名）" hint="アカウント名ではなく、宅配便を受け取れるお名前" error={errors.name}>
        <input className="gf-input" value={name} placeholder="例）山田 花子" onChange={(e) => setName(e.target.value)} maxLength={100} />
      </GfField>
      <div className="gf-proxy-grid">
        <GfField label="郵便番号" error={errors.postal}>
          <input className="gf-input is-mono" value={postal} inputMode="numeric" placeholder="123-4567" onChange={(e) => setPostal(e.target.value)} maxLength={10} />
        </GfField>
        <GfField label="電話番号" error={errors.phone}>
          <input className="gf-input is-mono" value={phone} inputMode="tel" onChange={(e) => setPhone(e.target.value)} maxLength={20} />
        </GfField>
      </div>
      <GfField label="住所" error={errors.address}>
        <input className="gf-input" value={address} placeholder="都道府県から建物名・部屋番号まで" onChange={(e) => setAddress(e.target.value)} maxLength={300} />
      </GfField>
      <GfField label="メールアドレス（任意）" hint="発送のお知らせ・専用クーポン・紹介者ページのログインに使います。アフィリ付き案件では発送時に自動でアフィリ登録されます" error={errors.email}>
        <input className="gf-input" type="email" value={email} inputMode="email" placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} maxLength={254} />
      </GfField>
      {choice ? (
        <GfField label="お届けするセット（クリエイターの希望）" error={errors.product}>
          <select className="gf-select" value={sku} onChange={(e) => setSku(e.target.value)}>
            <option value="">— 選んでください —</option>
            {campaign!.product_options.map((p) => (
              <option key={p.sku} value={p.sku}>
                {p.name}（{p.contents}）
              </option>
            ))}
          </select>
        </GfField>
      ) : (
        <div className="gf-field">
          <span className="gf-field-label">配送商品</span>
          <div className="gf-readonly">{product}</div>
        </div>
      )}
      <GfButton variant="primary" size="lg" block disabled={busy} onClick={() => void submit()}>
        {busy ? "登録中…" : GF_PHASE >= 2 ? "住所を登録（出荷内容を自動チェック）" : "住所を登録する"}
      </GfButton>
    </>
  );
}
